import { CampaignAssignedBy, CampaignRoutingSettings } from "@signalist/shared-types";

import { ROUTING_I18N } from "./campaignRoutingFields";
import { conditionsText, RoutingTranslate } from "./campaignRoutingText";

/** What the lead table knows about why a lead holds its campaign (`list_person`, PREC-4219). */
export interface RoutedLead {
	campaignId?: string | null;
	campaignAssignedBy?: CampaignAssignedBy | null;
	campaignRoutingRuleId?: number | null;
}

const ruleReason = (
	ruleId: number | null | undefined,
	settings: CampaignRoutingSettings | undefined,
	t: RoutingTranslate
): string => {
	if (!settings) {
		return t(`${ROUTING_I18N}.reason.ruleUnknown`);
	}

	const ordered = [...settings.rules].sort((a, b) => a.position - b.position);
	const index = ordered.findIndex((rule) => rule.id === ruleId);

	if (index === -1) {
		// The rule set was replaced since: the stored id names a rule the list no longer has.
		return t(`${ROUTING_I18N}.reason.ruleGone`);
	}

	return t(`${ROUTING_I18N}.reason.rule`, {
		number: index + 1,
		conditions: conditionsText(ordered[index].conditions, t),
	});
};

/**
 * Why the lead holds its campaign, as a sentence for its campaign tag, or null when there is
 * nothing to explain: no campaign, or one a person picked (USER, and NULL from before routing).
 */
export const routingReasonText = (
	lead: RoutedLead,
	settings: CampaignRoutingSettings | undefined,
	t: RoutingTranslate
): string | null => {
	const assignedBy = lead.campaignAssignedBy;

	if (!lead.campaignId || !assignedBy) {
		return null;
	}

	switch (assignedBy) {
		case "USER":
			return null;
		case "DEFAULT":
			return t(`${ROUTING_I18N}.reason.default`);
		case "RULE":
			return ruleReason(lead.campaignRoutingRuleId, settings, t);
		default: {
			const unknownSource: never = assignedBy;

			throw new Error(`Unknown campaign assignment source ${String(unknownSource)}`);
		}
	}
};
